import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase-client.ts';
import Header from '../../components/Header/index.js';
import BackButton from '../../components/BackButton/index.js';
import { parseChunkToChapterNum } from '../../utils/chunkParser.js';
import { orderMixpanelProperties } from '../../utils/mixpanelPropertyOrder.js';
import { logEngagementEvent } from '../../services/engagementTrackingService.js';
import './UniversalReadingPage.css';

// Toggle to show/hide CEFR level in subtitle (set to false to hide)
const SHOW_CEFR_LEVEL = false;
const HIGHLIGHT_CACHE_KEY = 'highlights_cache_v1';

const getHighlightCache = () => {
  try {
    const raw = localStorage.getItem(HIGHLIGHT_CACHE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (error) {
    return [];
  }
};

const writeHighlightCache = (entries) => {
  try {
    localStorage.setItem(HIGHLIGHT_CACHE_KEY, JSON.stringify(entries));
  } catch (error) {
  }
};

const syncHighlightCacheUserId = (userId) => {
  if (!userId) return;

  const cache = getHighlightCache();
  if (!cache.length) return;

  let updated = false;
  const normalized = cache.map(entry => {
    if (!entry.user_id || entry.user_id === 'anonymous') {
      updated = true;
      return { ...entry, user_id: userId };
    }
    return entry;
  });

  if (updated) {
    writeHighlightCache(normalized);
  }
};

const buildHighlightKey = (entry) => (
  [
    entry.user_id,
    entry.book_id,
    entry.chapter_num,
    entry.stanza_num,
    entry.start_idx,
    entry.end_idx,
  ].join('|')
);

const dedupeHighlights = (entries) => {
  const map = new Map();
  entries.forEach(entry => {
    map.set(buildHighlightKey(entry), entry);
  });
  return Array.from(map.values());
};

const buildHighlightMap = (entries) => entries.reduce((acc, entry) => {
  const key = String(entry.stanza_num);
  if (!acc[key]) {
    acc[key] = [];
  }
  acc[key].push(entry);
  return acc;
}, {});

const flushHighlightsToSupabase = async (userId = localStorage.getItem('auth_id')) => {
  const cache = getHighlightCache();
  if (!cache.length || !userId) return;

  const normalizedCache = cache.map(entry => (
    !entry.user_id || entry.user_id === 'anonymous'
      ? { ...entry, user_id: userId }
      : entry
  ));
  writeHighlightCache(normalizedCache);

  const pending = normalizedCache.filter(entry => entry.user_id === userId);
  if (!pending.length) return;

  const deduped = dedupeHighlights(pending);
  const { error } = await supabase
    .from('HIGHLIGHTS')
    .upsert(deduped, {
      onConflict: 'user_id,book_id,chapter_num,stanza_num,start_idx,end_idx',
      ignoreDuplicates: true,
    });
  if (error) return;

  const insertedKeys = new Set(deduped.map(buildHighlightKey));
  const remaining = normalizedCache.filter(entry => !insertedKeys.has(buildHighlightKey(entry)));
  writeHighlightCache(remaining);
};

const UniversalReadingPage = ({ 
  bookId,
  bookTitle,
  bookRoute,
  bookType,
  level,
  chunk,
  stanzas,
  summary,
  cssClass,
  StanzaComponent,
  SummaryComponent,
  onLogout,
  customTitle,
  customSubtitle
}) => {
  const navigate = useNavigate();
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  const [userInfo, setUserInfo] = useState({ email: '', cefr_level: '', auth_id: '' });
  const [stanzaStates, setStanzaStates] = useState({});
  const [highlightMap, setHighlightMap] = useState({});
  const [activeHighlightStanza, setActiveHighlightStanza] = useState(null);
  const [isAssessmentFooterOpen, setIsAssessmentFooterOpen] = useState(true);

  // 🔍 MIXPANEL TRACKING FUNCTION - Tracks page view
  const trackMixpanelEvent = (eventName, properties = {}, options = {}) => {
    const { includeCefrLevel = true } = options;
    const baseProperties = {
      event_timestamp: Date.now(),
      user_id: localStorage.getItem('auth_id') || 'anonymous',
      ...properties
    };

    if (includeCefrLevel) {
      baseProperties.cefr_level = localStorage.getItem('cefr_level') || 'unknown';
    }

    const orderedProperties = orderMixpanelProperties(baseProperties);

    if (window.mixpanel) {
      window.mixpanel.track(eventName, orderedProperties);
    }
    console.log(`🔍 Mixpanel Event: ${eventName}`, orderedProperties);
  };

  // 🔍 MIXPANEL TRACKING: Track page view on component mount
  useEffect(() => {
    trackMixpanelEvent('page_view', {
      book_title: bookTitle,
      level: level,
      chunk: chunk,
      stanza_count: stanzas?.length || 0,
      has_summary: !!summary
    });
  }, [bookTitle, level, chunk, stanzas, summary]);

  // Fetch user info on component mount
  useEffect(() => {
    const fetchUserInfo = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user && user.email) {
        // Fetch user info from USERS table
        const { data } = await supabase
          .from('USERS')
          .select('email, cefr_level, auth_id, class_id')
          .eq('email', user.email)
          .single();
        if (data) {
          setUserInfo({ email: data.email, cefr_level: data.cefr_level, auth_id: data.auth_id, class_id: data.class_id });
        }
      }
    };
    fetchUserInfo();
  }, []);

  const handleSummaryToggle = () => {
    setSummaryExpanded(prevExpanded => {
      const nextExpanded = !prevExpanded;

      if (nextExpanded) {
        const summaryEventProperties = {
          user_id: localStorage.getItem('auth_id') || 'anonymous',
          book_id: bookId || 'unknown',
          book_title: bookTitle || 'unknown',
          level: level || 'unknown',
          chunk: chunk || 'unknown',
          chapter_num: chapterNum,
        };

        void logEngagementEvent('summary_read', summaryEventProperties);

        trackMixpanelEvent('summary_read', summaryEventProperties, { includeCefrLevel: false });
      }

      return nextExpanded;
    });
  };

  const handleSummaryAudioPlay = () => {
    trackMixpanelEvent('audio_play', {
      book_title: bookTitle || 'unknown',
      chunk: chunk || 'unknown',
    });
  };

  const handleAssessmentClick = () => {
    const userLevel = level || userInfo.cefr_level || 'B2';
    const chunkParam = chunk ? `/${chunk}` : '';
    const routeBase = bookRoute.startsWith('/') ? bookRoute : `/${bookRoute}`;
    trackMixpanelEvent('assessment_start', {
      book_title: bookTitle || 'unknown',
      chunk: chunk || 'unknown',
    });

    if (window.mixpanel) {
      window.mixpanel.start_batch_senders();
    }

    navigate(`${routeBase}/assessment/${userLevel}${chunkParam}?auth_id=${userInfo.auth_id}`);
  };

  const handleBackToChapters = () => {
    const routeBase = bookRoute.startsWith('/') ? bookRoute : `/${bookRoute}`;
    navigate(routeBase);
  };

  useEffect(() => {
    if (!userInfo.auth_id) return;
    localStorage.setItem('auth_id', userInfo.auth_id);
    if (userInfo.cefr_level) {
      localStorage.setItem('cefr_level', userInfo.cefr_level);
    }
    syncHighlightCacheUserId(userInfo.auth_id);
  }, [userInfo.auth_id, userInfo.cefr_level]);

  const handleLogout = async () => {
    await flushHighlightsToSupabase(userInfo.auth_id);
    onLogout();
    navigate('/');
  };

  const handleContentSwitch = (stanzaNumber, isShowingAdapted) => {
    // Ensure stanzaNumber is treated as string for consistent key handling
    const stanzaKey = String(stanzaNumber);
    
    setStanzaStates(prevStates => {
      const newStates = {
        ...prevStates,
        [stanzaKey]: isShowingAdapted
      };
      return newStates;
    });
  };

  const handleHighlightModeChange = (stanzaNumber, isActive) => {
    setActiveHighlightStanza(isActive ? stanzaNumber : null);
  };

  // Generate title and subtitle
  const title = customTitle || (level && chunk ? 
    `Chapter ${parseChunkToChapterNum(chunk) || '?'} - ${level} Level` : 
    bookTitle);
  
  const subtitle = customSubtitle || (SHOW_CEFR_LEVEL ? 
    (level ? `Stanzas 1 - ${stanzas.length} | CEFR Level: ${level}` : `CEFR Level B2 | Stanza Range: 1-${stanzas.length}`) : 
    `Stanzas 1 - ${stanzas.length}`);

  const chapterNum = parseChunkToChapterNum(chunk);

  useEffect(() => {
    if (!bookId || !chapterNum || !stanzas.length) return;

    const stanzaNumbers = stanzas
      .map(stanza => Number(stanza.number))
      .filter(num => Number.isFinite(num));

    const cache = getHighlightCache();
    const cachedHighlights = cache.filter(entry => (
      entry.book_id === bookId &&
      entry.chapter_num === chapterNum &&
      stanzaNumbers.includes(Number(entry.stanza_num))
    ));

    setHighlightMap(buildHighlightMap(cachedHighlights));

    const fetchHighlights = async () => {
      if (!userInfo.auth_id) return;

      const { data, error } = await supabase
        .from('HIGHLIGHTS')
        .select('user_id, book_id, chapter_num, stanza_num, start_idx, end_idx, quote')
        .eq('user_id', userInfo.auth_id)
        .eq('book_id', bookId)
        .eq('chapter_num', chapterNum)
        .in('stanza_num', stanzaNumbers);

      if (error) {
        return;
      }

      const merged = dedupeHighlights([...(data || []), ...cachedHighlights]);
      setHighlightMap(buildHighlightMap(merged));
    };

    fetchHighlights();
  }, [bookId, chapterNum, stanzas, userInfo.auth_id]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      flushHighlightsToSupabase(userInfo.auth_id);
    };

    const handlePageHide = () => {
      flushHighlightsToSupabase(userInfo.auth_id);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        flushHighlightsToSupabase(userInfo.auth_id);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [userInfo.auth_id]);

  const handleHighlightCreated = (entry) => {
    setHighlightMap(prev => {
      const key = String(entry.stanza_num);
      const existing = prev[key] || [];
      const entryKey = buildHighlightKey(entry);
      if (existing.some(item => buildHighlightKey(item) === entryKey)) {
        return prev;
      }
      return {
        ...prev,
        [key]: [...existing, entry],
      };
    });
  };

  return (
    <div className={cssClass}>
      <Header userInfo={userInfo} onLogout={handleLogout} />

      <BackButton 
        onClick={handleBackToChapters} 
        text="Back to Chapter List"
        onBeforeClick={() => {
          // Start Mixpanel batch senders before navigation
          if (window.mixpanel) {
            window.mixpanel.start_batch_senders();
          }
        }}
      />

      <div className="container">
        <h1 className="title">{title}</h1>
        <p className="subtitle">{subtitle}</p>

        <div className="summary-section">
          <SummaryComponent
            expanded={summaryExpanded}
            onToggle={handleSummaryToggle}
            content={typeof summary === 'string' ? summary : summary?.content || ''}
            title={typeof summary === 'object' ? summary?.title : ''}
            keyThemes={typeof summary === 'object' ? summary?.keyThemes || [] : []}
            characters={typeof summary === 'object' ? summary?.characters || [] : []}
            audioSrc={typeof summary === 'object' ? summary?.audioSrc : ''}
            audioTitle={typeof summary === 'object' ? summary?.audioTitle : ''}
            onAudioPlay={handleSummaryAudioPlay}
            showChunkInfo={false}
            showCefrInfo={false}
          />
        </div>

        <div className="stanza-container">
          {stanzas.map((stanza, index) => {
            const stanzaNumber = stanza.number;
            const stanzaKey = String(stanzaNumber);
            const isShowingAdapted = stanzaStates[stanzaKey] === undefined ? true : stanzaStates[stanzaKey];
            const displayText = isShowingAdapted && stanza.adaptedText && stanza.adaptedText.trim()
              ? stanza.adaptedText
              : stanza.originalText;
            const highlightEntries = highlightMap[stanzaKey] || [];

            return (
              <StanzaComponent
                key={stanza.id}
                number={stanzaNumber}
                chapterIndex={index + 1}
                chapterTotal={stanzas.length}
                bookId={bookId}
                bookTitle={bookTitle}
                chapterNum={Number.isNaN(chapterNum) ? null : chapterNum}
                highlightEntries={highlightEntries}
                onHighlightCreated={handleHighlightCreated}
                highlightActiveStanza={activeHighlightStanza}
                onHighlightModeChange={handleHighlightModeChange}
                text={displayText}
                originalText={stanza.originalText}
                adaptedText={stanza.adaptedText}
                hasImage={stanza.hasImage || false}
                imageUrl={stanza.imageUrl || ''}
                imageDescription={stanza.imageDescription || ''}
                bookType={bookType}
                onContentSwitch={(stanzaNumber, isShowingAdapted) => {
                  handleContentSwitch(stanzaNumber, isShowingAdapted);
                }}
                initialIsShowingAdapted={isShowingAdapted}
              />
            );
          })}
        </div>
      </div>

      <div className={`sticky-footer ${isAssessmentFooterOpen ? '' : 'sticky-footer-collapsed'}`}>
        <button
          className="assessment-footer-toggle"
          type="button"
          onClick={() => setIsAssessmentFooterOpen(prevOpen => !prevOpen)}
          aria-expanded={isAssessmentFooterOpen}
          aria-label={isAssessmentFooterOpen ? 'Hide assessment footer' : 'Show assessment footer'}
          title={isAssessmentFooterOpen ? 'Hide assessment footer' : 'Show assessment footer'}
        >
          <i className={`fa-solid fa-circle-chevron-${isAssessmentFooterOpen ? 'down' : 'up'}`} />
        </button>
        <div className="footer-content">
          <p className="instruction-text">
            Ready to test your understanding of this chapter?
          </p>
          <button className="quiz-btn" onClick={handleAssessmentClick}>
            Go to Assessment
          </button>
        </div>
      </div>
    </div>
  );
};

export default UniversalReadingPage;
